// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CredentialBilling } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import Loading from '../../components/Loading';
import { MoreActionsMenu } from '../../components/MoreActionsMenu';
import PartyDisplay from '../../components/PartyDisplay';
import { DataTable } from '../../components/Table/DataTable';
import useCoins from '../../hooks/queries/cn/useCoin';
import useCredentialBillings from '../../hooks/queries/credential/billing/useCredentialBillings';
import useUserService from '../../hooks/queries/credential/onboarding/useUserService';
import TemplateContract from '../../utils/TemplateContract';
import { extractPrefix, fmt, parseDateString } from '../../utils/common';
import { PageWrapper } from '../PageWrapper';
import { GenericAction } from '../collateral/collaterals/types';
import { DistributeAmuletBillingDialog } from './DistributeAmuletBillingDialog';
import { TopUpLockedDepositDialog } from './TopUpLockedDepositDialog';
import { useCreateBillingActions } from './useCreateBillingActions';

type Row = TemplateContract<CredentialBilling>;

const createHeaders: (
  createActions: (r: Row) => GenericAction<Row>[],
  setRow: React.Dispatch<React.SetStateAction<Row | undefined>>,
) => GridColDef<Row>[] = (createActions, setRow) => {
  const setSelectedRow = (r: Row) => {
    setRow(r);
  };

  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);

    return <MoreActionsMenu actions={actions} row={params.row} setSelectedRow={setSelectedRow} />;
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => {
        return <PartyDisplay party={params.row.payload.operator} />;
      },
      valueGetter: (params) => extractPrefix(params.row.payload.operator, '::'),
      flex: 100,
      filterable: true,
    },
    {
      field: 'issuer',
      headerName: 'Issuer',
      renderCell: (params) => <PartyDisplay party={params.row.payload.issuer} />,
      valueGetter: (params) => extractPrefix(params.row.payload.issuer, '::'),
      flex: 100,
      filterable: true,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
      valueGetter: (params) => extractPrefix(params.row.payload.holder, '::'),
      flex: 100,
      filterable: true,
    },
    {
      field: 'id',
      headerName: 'Id',
      valueGetter: (params) => params.row.payload.credentialId,
      flex: 80,
      filterable: true,
    },
    {
      field: 'currentDeposit',
      headerName: 'Current Deposit',
      renderCell: (params) =>
        `${fmt(params.row.payload.balanceState.currentDepositAmountCc, 2)} CC`,
      flex: 100,
      valueGetter: (params) => Number(params.row.payload.balanceState.currentDepositAmountCc),
      filterable: false,
    },
    {
      field: 'totalPaid',
      headerName: 'Total Paid',
      renderCell: (params) => fmt(params.row.payload.balanceState.totalCredentialFeesPaidCc, 2),
      valueGetter: (params) => Number(params.row.payload.balanceState.totalCredentialFeesPaidCc),
      flex: 100,
      filterable: false,
    },
    {
      field: 'Deposit target amount (USD)',
      headerName: 'Deposit target amount (USD)',
      renderCell: (params) => fmt(params.row.payload.params.depositTargetAmountUsd, 2),
      valueGetter: (params) => Number(params.row.payload.params.depositTargetAmountUsd),
      flex: 100,
      filterable: false,
    },
    {
      field: 'Fee per day',
      headerName: 'Fee per day',
      renderCell: (params) => fmt(params.row.payload.params.feePerDayUsd.rate, 2),
      valueGetter: (params) => Number(params.row.payload.params.feePerDayUsd.rate),
      flex: 100,
      filterable: false,
    },
    {
      field: 'lastBilledAt',
      headerName: 'Last Billed At',
      valueGetter: (params) => {
        if (params.row.payload.billingState.status.tag === 'New') {
          return null;
        }
        return parseDateString(params.row.payload.billingState.lastBilledAt).toLocaleString();
      },
      flex: 150,
      filterable: false,
    },
    {
      field: 'status',
      headerName: 'Billing Status',
      valueGetter: (params) => params.row.payload.billingState.status.tag,
      renderCell: (params) => {
        if (params.row.payload.billingState.status.tag === 'Failure') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Tooltip
                sx={{ cursor: 'pointer' }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      p: 1,
                      opacity: 0,
                      backgroundColor: 'black',
                      borderRadius: 1,
                    },
                  },
                }}
                title={
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography fontWeight="bold" sx={{ mb: 1 }} variant="body2" color="error">
                      Failure
                    </Typography>
                    <Typography variant="caption">
                      {params.row.payload.billingState.status.value.reason}
                    </Typography>
                  </Box>
                }
              >
                <InfoOutlined fontSize="small" color="error" />
              </Tooltip>
              <Typography variant="body2" color="error">
                Failure
              </Typography>
            </Box>
          );
        }
        return (
          <Typography variant="body2" color="green">
            {params.row.payload.billingState.status.tag}
          </Typography>
        );
      },
      flex: 100,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: renderActionCell,
      sortable: false,
      filterable: false,
      flex: 50,
      minWidth: 60,
    },
  ];
};
interface BillingProps {
  disableVirtualization?: boolean;
}
export const Billing: React.FC<BillingProps> = ({ disableVirtualization }) => {
  const [openDistributeCredentialBillingDialog, setOpenDistributeCredentialBillingDialog] =
    useState<boolean>(false);
  const [openTopupDepositDialog, setOpenTopupDepositDialog] = useState<boolean>(false);
  const [getRow, setRow] = useState<Row>();
  const userService = useUserService();
  const billings = useCredentialBillings();
  const amulets = useCoins();

  const handleTopUp = (r: Row) => {
    setOpenTopupDepositDialog(true);
    setRow(r);
  };

  const handleDistribute = (r: Row) => {
    setOpenDistributeCredentialBillingDialog(true);
    setRow(r);
  };

  const { createActions } = useCreateBillingActions({
    handleDistributeClick: handleDistribute,
    handleTopUpClick: handleTopUp,
  });

  const isLoading = billings.isLoading || userService.isLoading;
  const totalBalance = amulets.data
    ? amulets.data.reduce((acc, c) => acc + parseFloat(c.payload.amount.initialAmount), 0)
    : 0;
  if (isLoading) return <Loading />;

  return (
    <PageWrapper pageName="Billings">
      <DistributeAmuletBillingDialog
        open={openDistributeCredentialBillingDialog}
        onClose={() => setOpenDistributeCredentialBillingDialog(false)}
        row={getRow}
        amulets={amulets.data}
        totalBalanceCC={totalBalance}
      />
      <TopUpLockedDepositDialog
        open={openTopupDepositDialog}
        onClose={() => setOpenTopupDepositDialog(false)}
        row={getRow}
        amulets={amulets.data}
      />
      <DataTable
        columns={createHeaders(createActions, setRow)}
        rows={billings.data || []}
        rowKey={(a) => a.contractId}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
